import { Link, useNavigate } from "@tanstack/react-router";
import { MenuIcon, CloseIcon, LogOutIcon, LayoutDashboardIcon, LogoMark } from "@/components/ui/TriIcons";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { brand } from "@/lib/brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Building2, Store, Shield, ArrowRight } from "lucide-react";

import { products } from "@/lib/product-data";

const WINE = "#7B1416";
const CREAM = "#F5EFE4";
const CHARCOAL = "#1C1917";
const SAND = "#C4A882";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', 'Playfair Display', serif";

const sectionLinks = [
  { href: "#ecosystem", label: "Ecosystem" },
  { href: "#experience", label: "Experience" },
  { href: "#intelligence", label: "Intelligence" },
  { href: "#analytics", label: "Analytics" },
  { href: "#implementation", label: "Implementation" },
];

const mobileLinks: { to: string; label: string; hash?: string }[] = [
  ...sectionLinks.map(s => ({ to: "/", label: s.label, hash: s.href })),
];

function BrandMark({ size = "md", logoUrl }: { size?: "sm" | "md"; logoUrl?: string | null }) {
  const dim = size === "sm" ? "h-8 w-8" : "h-9 w-9";
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${brand.name} logo`}
        className={`${dim} rounded-[10px] object-contain shadow-elegant`}
      />
    );
  }
  return (
    <div className={`relative ${dim} shrink-0`}>
      <div
        className="absolute inset-0 rounded-[11px] shadow-elegant"
        style={{
          background: `linear-gradient(135deg, ${WINE} 0%, #5C0F11 60%, ${WINE} 100%)`,
        }}
      />
      <div
        className="absolute inset-0 rounded-[11px]"
        style={{
          background:
            "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, transparent 55%)",
        }}
      />
      <div className="absolute inset-[2px] rounded-[9px] bg-white/[0.06] backdrop-blur-[2px]" />
      <LogoMark
        className="absolute inset-0 m-auto h-5 w-5"
        accent={SAND}
        style={{ color: "rgba(255,255,255,0.92)" }}
      />
    </div>
  );
}

export function Navbar({
  brandName = brand.name,
  tagline = brand.tagline,
  logoUrl = null,
}: {
  brandName?: string;
  tagline?: string;
  logoUrl?: string | null;
} = {}) {
  const { user, profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? "U";

  const roleLabel = profile?.role === "school_admin" ? "School Admin"
    : profile?.role === "superadmin" ? "Admin"
    : profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
    : "User";

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4"
      >
        <motion.nav
          animate={{
            paddingTop: scrolled ? 8 : 12,
            paddingBottom: scrolled ? 8 : 12,
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="flex w-full max-w-[1280px] items-center justify-between rounded-2xl border px-4 sm:px-5"
          style={{
            borderColor: scrolled ? "rgba(28,25,23,0.12)" : "rgba(28,25,23,0.05)",
            background: scrolled ? "rgba(245,239,228,0.92)" : "rgba(245,239,228,0.75)",
            backdropFilter: "blur(24px) saturate(160%)",
            WebkitBackdropFilter: "blur(24px) saturate(160%)",
            boxShadow: scrolled ? "0 4px 24px -4px rgba(123,20,22,0.10)" : "none",
          }}
          onMouseLeave={() => setHoveredGroup(null)}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark logoUrl={logoUrl} />
            <div className="leading-tight">
              <div className="text-lg font-bold tracking-tight" style={{ color: CHARCOAL, fontFamily: FONT_DISPLAY }}>{brandName}</div>
              <div className="text-[8px] uppercase tracking-[0.2em] font-semibold" style={{ color: WINE }}>
                {tagline}
              </div>
            </div>
          </Link>

          {/* Desktop nav — section-based + Products dropdown */}
          <div className="hidden items-center gap-1 lg:flex">
            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setHoveredGroup("Products")}
              onMouseLeave={() => setHoveredGroup(null)}
            >
              <button
                className="rounded-full px-4 py-2 text-sm text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground"
              >
                Products
              </button>
              <AnimatePresence>
                {hoveredGroup === "Products" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute left-1/2 top-full mt-2 -translate-x-1/2 pt-2"
                  >
                    <div
                      className="w-[480px] rounded-2xl border p-3"
                      style={{
                        borderColor: "rgba(26,26,26,0.06)",
                        background: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(24px) saturate(180%)",
                        WebkitBackdropFilter: "blur(24px) saturate(180%)",
                        boxShadow: "0 12px 40px -8px rgba(26,26,26,0.12)",
                      }}
                    >
                      <div className="grid grid-cols-2 gap-1">
                        {products.map((p) => {
                          const Icon = p.icon;
                          return (
                            <Link
                              key={p.id}
                              to="/ecosystem/$productId"
                              params={{ productId: p.id }}
                              className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-black/[0.03]"
                            >
                              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: `${p.color}12` }}>
                                <Icon className="h-5 w-5" accent={p.color} style={{ color: p.color }} />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-foreground">{p.shortName}</div>
                                <div className="text-xs text-muted-foreground line-clamp-1">{p.tagline}</div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Section links — scroll to landing sections */}
            {sectionLinks.map((s) => (
              <a
                key={s.href}
                href={s.href}
                className="rounded-full px-3 py-2 text-sm text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground"
              >
                {s.label}
              </a>
            ))}

            {/* Portals dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setHoveredGroup("Portals")}
              onMouseLeave={() => setHoveredGroup(null)}
            >
              <button
                className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground"
              >
                Portals
              </button>
              <AnimatePresence>
                {hoveredGroup === "Portals" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute left-1/2 top-full mt-2 -translate-x-1/2 pt-2"
                  >
                    <div
                      className="w-72 rounded-2xl border p-3"
                      style={{
                        borderColor: "rgba(26,26,26,0.06)",
                        background: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(24px) saturate(180%)",
                        WebkitBackdropFilter: "blur(24px) saturate(180%)",
                        boxShadow: "0 12px 40px -8px rgba(26,26,26,0.12)",
                      }}
                    >
                      <div className="px-2 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sign in to a portal</div>
                      {[
                        { key: "school_admin", label: "School Portal", desc: "Manage students, collections, merchants", icon: Building2, color: WINE, to: "/auth?portal=school_admin" },
                        { key: "merchant", label: "Merchant Portal", desc: "Accept payments, manage POS, settle daily", icon: Store, color: SAND, to: "/auth?portal=merchant" },
                        { key: "superadmin", label: "Platform Admin", desc: "Oversee tenants, branding, analytics", icon: Shield, color: WINE, to: "/auth?portal=superadmin" },
                      ].map((p) => {
                        const Icon = p.icon;
                        return (
                          <Link
                            key={p.key}
                            to={p.to}
                            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-black/[0.03]"
                          >
                            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: `${p.color}12` }}>
                              <Icon className="h-5 w-5" style={{ color: p.color }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-foreground">{p.label}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{p.desc}</div>
                            </div>
                            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition group-hover:translate-x-0.5 group-hover:text-foreground" />
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

          {/* Right side actions */}
          <div className="hidden items-center gap-2 lg:flex">
            <button
              className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full bg-black/[0.04] py-1.5 pl-1.5 pr-3 text-sm transition hover:bg-black/[0.06]">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-[var(--forest)] text-xs text-white">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="max-w-[100px] truncate">{profile?.full_name?.split(" ")[0] ?? "User"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    {user.email}
                    <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px]">{roleLabel}</span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/app" className="flex items-center gap-2">
                      <LayoutDashboardIcon className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOutIcon className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="rounded-full">
                  <Link to="/auth">Sign in</Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="rounded-full shadow-sm"
                  style={{ background: WINE, color: "#fff", height: 36, border: "none", fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', system-ui, sans-serif" }}
                >
                  <Link to="/auth">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </motion.nav>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] overflow-y-auto p-6 pt-24"
              style={{ background: "#FFFFFF" }}
            >
              <div className="flex flex-col gap-1">
                {mobileLinks.map((l) => (
                  l.hash ? (
                    <a
                      key={l.hash}
                      href={l.hash}
                      onClick={() => setOpen(false)}
                      className="rounded-xl px-4 py-3 text-sm hover:bg-black/[0.03]"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="rounded-xl px-4 py-3 text-sm hover:bg-black/[0.03]"
                    >
                      {l.label}
                    </Link>
                  )
                ))}
                <div className="mt-2 border-t pt-2">
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Portals</div>
                  {[
                    { label: "School Portal", to: "/auth?portal=school_admin" },
                    { label: "Merchant Portal", to: "/auth?portal=merchant" },
                    { label: "Platform Admin", to: "/auth?portal=superadmin" },
                  ].map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setOpen(false)}
                      className="rounded-xl px-4 py-3 text-sm hover:bg-black/[0.03]"
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {user ? (
                    <>
                      <Button asChild variant="outline" className="w-full justify-start">
                        <Link to="/app" onClick={() => setOpen(false)}>
                          <LayoutDashboardIcon className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </Button>
                      <Button variant="ghost" onClick={handleSignOut} className="w-full justify-start text-destructive hover:text-destructive">
                        <LogOutIcon className="mr-2 h-4 w-4" />
                        Sign out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/auth" onClick={() => setOpen(false)}>Sign in</Link>
                      </Button>
                      <Button asChild className="w-full" style={{ background: WINE, border: "none", color: "#fff" }}>
                        <Link to="/auth" onClick={() => setOpen(false)}>Get Started</Link>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function Footer({
  brandName = brand.name,
  descriptor = brand.descriptor,
  city = brand.city,
  country = brand.country,
  company = brand.company,
  domain = brand.domain,
  logoUrl = null,
}: {
  brandName?: string;
  tagline?: string;
  descriptor?: string;
  city?: string;
  country?: string;
  company?: string;
  domain?: string;
  logoUrl?: string | null;
} = {}) {
  const year = new Date().getFullYear();
  const cols = [
    {
      title: "Products",
      links: products.map(p => ({ label: p.name, to: "/ecosystem", productId: p.id })),
    },
    {
      title: "Solutions",
      links: [
        { label: "For Parents", to: "/parents" },
        { label: "For Schools", to: "/schools" },
        { label: "For Merchants", to: "/merchants" },
        { label: "For Developers", to: "/documents" },
        { label: "Interactive Demos", to: "/demo" },
      ],
    },
    {
      title: "Portals",
      links: [
        { label: "School Portal", to: "/auth?portal=school_admin" },
        { label: "Merchant Portal", to: "/auth?portal=merchant" },
        { label: "Platform Admin", to: "/auth?portal=superadmin" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", to: "/about" },
        { label: "Pricing", to: "/pricing" },
        { label: "Careers", to: "/about" },
        { label: "Security", to: "/about" },
        { label: "Support", to: "/about" },
      ],
    },
  ];

  return (
    <footer className="relative overflow-hidden text-white" style={{ background: CHARCOAL }}>
      {/* Ambient gradient mesh */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 60% 40% at 15% 0%, rgba(123,20,22,0.10) 0%, transparent 50%),
            radial-gradient(ellipse 50% 40% at 85% 100%, rgba(196,168,130,0.06) 0%, transparent 50%),
            radial-gradient(ellipse 40% 30% at 50% 50%, rgba(123,20,22,0.03) 0%, transparent 50%)
          `,
        }}
      />

      {/* Subtle top border glow */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, rgba(196,168,130,0.3), transparent)` }} />

      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-8">
        {/* Top section — brand + newsletter */}
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-20">
          <div>
            <div className="flex items-center gap-3">
              <BrandMark size="md" logoUrl={logoUrl} />
              <div className="leading-tight">
                <div className="font-display text-2xl tracking-tight">{brandName}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/30">Group (U) LTD</div>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm leading-[1.7] text-white/40">
              {descriptor}
            </p>
            <p className="mt-6 flex items-center gap-2 text-xs text-white/30">
              <span className="inline-block h-1 w-1 rounded-full" style={{ background: "rgba(196,168,130,0.6)" }} />
              Built in {city}, {country}. Designed for Africa. Ready for the World.
            </p>

            {/* Social icons */}
            <div className="mt-8 flex items-center gap-3">
              {["X", "in", "f", "IG"].map((s) => (
                <a
                  key={s}
                  href="#"
                  className="grid h-10 w-10 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-xs font-semibold text-white/40 transition-all hover:border-white/20 hover:bg-white/[0.06] hover:text-white/80"
                >
                  {s}
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter card */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-8">
            <h3 className="font-display text-lg font-medium text-white/90">Stay informed</h3>
            <p className="mt-2 text-sm text-white/40">Product updates, insights, and announcements — monthly.</p>
            <div className="mt-5 flex gap-2">
              <input
                type="email"
                placeholder="you@school.ac.ug"
                className="flex-1 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-2.5 text-sm text-white/80 outline-none transition-all placeholder:text-white/20 focus:border-white/20"
              />
              <button
                className="rounded-xl px-5 py-2.5 text-sm font-medium text-white transition-all hover:opacity-90"
                style={{ background: WINE }}
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Link columns */}
        <div className="mt-16 grid gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {cols.map((col) => (
            <div key={col.title}>
              <div className="text-xs font-semibold uppercase tracking-[0.15em] text-white/50">{col.title}</div>
              <ul className="mt-5 space-y-3 text-sm text-white/35">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.productId ? (
                      <Link to="/ecosystem/$productId" params={{ productId: l.productId }} className="transition-all hover:text-white/80 hover:translate-x-0.5">{l.label}</Link>
                    ) : (
                      <Link to={l.to} className="transition-all hover:text-white/80 hover:translate-x-0.5">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-white/50">Legal</div>
            <ul className="mt-5 space-y-3 text-sm text-white/35">
              <li><a href="#" className="transition-all hover:text-white/80">Privacy Policy</a></li>
              <li><a href="#" className="transition-all hover:text-white/80">Terms of Service</a></li>
              <li><a href="#" className="transition-all hover:text-white/80">Data Protection</a></li>
              <li><a href="#" className="transition-all hover:text-white/80">Security</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-8 sm:flex-row">
          <div className="text-xs text-white/25">
            © {year} {company}. All rights reserved.
          </div>
          <div className="flex items-center gap-6 text-xs text-white/25">
            <span>{brandName} · {domain}</span>
            <span className="flex items-center gap-1.5">
              <motion.span
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="h-1.5 w-1.5 rounded-full bg-green-400"
              />
              All systems operational
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
